<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
import { FlexibleScenario, Scenario } from "@reco/shared/src/constants";
import { Feature } from "~/composables/FeatureToggle/Feature";
import { useFeatureToggle } from "~/composables/FeatureToggle/useFeatureToggle";
import { useProduct } from "~/composables/useProduct";
import { useRecoConfig } from "~/composables/useRecoConfig";

const { locale, country, language } = useRecoConfig();
const { product, erpNumber } = useProduct();
const { isFeatureEnabled } = useFeatureToggle();
const { products, productsPending, cross, crossPending, category, categoryPending, brands, brandsPending, headings } =
  useRecommendations(product, erpNumber);

const { t } = useI18n();
const showMoreBtnLabel = computed(() => t("reco-frontend-next.RDP.show_more"));

const localeObj = {
  locale,
  country,
  language,
};

const checkFeatureToggle = () => {
  if (isFeatureEnabled(Feature.enableLogging)) {
    console.log("Feature Toggle: Logging enabled");
  }
};

useHead({
  title: () => product.value?.fullTitle || getProductTitleFromURL(),
});

onMounted(() => {
  setNavLangUrls(localeObj);

  checkFeatureToggle();
});
</script>

<template>
  <section class="r-center-content">
    <aside class="r-card-wrapper" aria-labelledby="product-card__header">
      <ProductCard v-if="product" :locale-context="localeObj" :product="product" />
    </aside>
    <main class="r-grids-wrapper">
      <ProductsGrid
        v-if="productsPending || products.length"
        :products="products"
        :locale-context="localeObj"
        :heading="headings.products"
        :show-more-text="showMoreBtnLabel"
        :tracking="getTrackingValue(Scenario.PRODUCTS)"
        :is-loading="productsPending"
      />

      <ProductsGrid
        v-if="crossPending || cross.length"
        :products="cross"
        :locale-context="localeObj"
        :heading="headings.cross"
        :show-more-text="showMoreBtnLabel"
        :tracking="getTrackingValue(Scenario.CROSS)"
        :is-loading="crossPending"
      />

      <ProductsGrid
        v-if="categoryPending || category.length"
        :products="category"
        :locale-context="localeObj"
        :heading="headings.category"
        :show-more-text="showMoreBtnLabel"
        :tracking="getTrackingValue(FlexibleScenario.BESTSELLERS_CATEGORY_2_PRODUCTBASED_URL)"
        :is-loading="categoryPending"
      />

      <ProductsGrid
        v-if="brandsPending || brands.length"
        :products="brands"
        :locale-context="localeObj"
        :heading="headings.brands"
        :show-more-text="showMoreBtnLabel"
        :tracking="getTrackingValue(FlexibleScenario.BESTSELLERS_BRAND_PRODUCTBASED_URL)"
        :is-loading="brandsPending"
      />
    </main>
  </section>
</template>

<style lang="scss">
body {
  font-family: "LidlFontCondPro", "Trebuchet MS", sans-serif;
}

.r-center-content {
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  box-sizing: border-box;

  display: flex;
  flex-direction: column;

  @media (min-width: $ods-breakpoint-tablet-landscape-min) {
    flex-direction: row;
  }

  .r-card-wrapper {
    align-self: flex-start;
    opacity: 1;
    padding: 0;
    position: relative;
    width: 100%;

    @media (min-width: $ods-breakpoint-tablet-landscape-min) {
      padding: 0 var(--ods-space-16) 0 0;
      position: sticky;
      top: 0;
      width: 25%;
    }
  }

  .r-grids-wrapper {
    width: 100%;

    @media (min-width: $ods-breakpoint-tablet-landscape-min) {
      width: 75%;
    }
  }
}
</style>
